import { useMemo, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { LockIcon } from 'lucide-react';
import { Button, ErrorNotice } from './primitives';

/**
 * The Stripe payment form every BCE app uses, laid out like Stripe Checkout:
 * bank and card fields show at once, and nothing is charged until the payer
 * presses Pay. Pressing Pay makes a Stripe confirmation token (the payer's
 * details, not yet charged) and asks the caller's API for a quote; when a
 * credit-card surcharge applies, the fee and new total are shown and the
 * payer confirms them before anything is charged. The API then charges
 * exactly that total.
 *
 * Card and bank details go straight into Stripe's own fields (iframes); they
 * never touch BCE's page code or servers.
 *
 * Bank (ACH) is offered first — BCE's preferred method, and never surcharged.
 */

/** What a payment costs once the payment method is known. */
export interface StripePaymentQuote {
  readonly amount: number;
  readonly fee: number;
  readonly total: number;
  readonly feePercent: number;
  /** Sent back unchanged when paying, so the charge is exactly what was shown. */
  readonly totalCents: number;
}

/** What the caller's API returns once it has charged. */
export interface StripeChargeResult {
  readonly paymentIntentId: string;
  readonly clientSecret: string | null;
  readonly status: string;
}

/** How the payment ended: `succeeded`, or `processing` for a bank payment that takes days to clear. */
export type StripePaymentOutcome = 'succeeded' | 'processing';

export interface StripePaymentProps {
  readonly publishableKey: string;
  /** What will be charged toward the balance, in cents; null while the payer's choice isn't valid (Pay stays off). */
  readonly amountCents: number | null;
  /** Shown on the pay button, already formatted ("$3,019.00"). */
  readonly amountLabel: string;
  /** The credit-card surcharge, said up front; 0 = none. */
  readonly cardSurchargePercent?: number;
  /** Formats dollars for the fee box ("$114.00"). */
  readonly formatMoney: (dollars: number) => string;
  /** Checks the rest of the page before charging; returns why not, or null to go ahead. */
  readonly beforePay?: () => string | null;
  /** Prices the payment once the payer's details are known; nothing is charged. */
  readonly quote: (confirmationTokenId: string) => Promise<StripePaymentQuote>;
  /** Charges exactly the quoted total. */
  readonly pay: (confirmationTokenId: string, expectedTotalCents: number) => Promise<StripeChargeResult>;
  /** Records the payment on the server once Stripe accepts it; returns the intent's status. */
  readonly confirmPayment: (paymentIntentId: string) => Promise<string>;
  readonly onComplete: (outcome: StripePaymentOutcome) => void;
  /** Hears a fee quote while it's on screen (and null when it's withdrawn), so the page can show the new total too. */
  readonly onFeeQuote?: (quote: StripePaymentQuote | null) => void;
  /** Brand color for the Stripe fields. */
  readonly accentColor?: string;
}

/** The methods BCE takes, in the order offered. Must match what the server's intent allows. */
const PAYMENT_METHOD_TYPES = ['us_bank_account', 'card'];
/** Stripe needs a positive amount to render; used only while Pay is off. */
const PLACEHOLDER_CENTS = 100;
const INTENT_STATUS = {
  succeeded: 'succeeded',
  processing: 'processing',
  requiresAction: 'requires_action',
  requiresPaymentMethod: 'requires_payment_method',
} as const;

/** Stripe wants one loader per publishable key, not one per render. */
let stripeCache: { key: string; promise: Promise<Stripe | null> } | null = null;
function stripeFor(key: string): Promise<Stripe | null> {
  if (!stripeCache || stripeCache.key !== key) stripeCache = { key, promise: loadStripe(key) };
  return stripeCache.promise;
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function PayForm({
  amountCents,
  amountLabel,
  cardSurchargePercent = 0,
  formatMoney,
  beforePay,
  quote,
  pay,
  confirmPayment,
  onComplete,
  onFeeQuote,
}: Omit<StripePaymentProps, 'publishableKey' | 'accentColor'>): React.ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A quote with a fee, waiting for the payer to accept it. */
  const [pendingFee, setPendingFeeState] = useState<{ tokenId: string; quote: StripePaymentQuote } | null>(null);
  function setPendingFee(next: { tokenId: string; quote: StripePaymentQuote } | null): void {
    if (next === null && pendingFee === null) return;
    setPendingFeeState(next);
    onFeeQuote?.(next?.quote ?? null);
  }

  async function charge(tokenId: string, quoted: StripePaymentQuote): Promise<void> {
    if (!stripe) return;
    setBusy(true);
    setError(null);
    try {
      const result = await pay(tokenId, quoted.totalCents);
      let status = result.status;
      // A card may need the bank's own check (3-D Secure) in the browser.
      if (status === INTENT_STATUS.requiresAction && result.clientSecret) {
        const { error: actionError, paymentIntent } = await stripe.handleNextAction({ clientSecret: result.clientSecret });
        if (actionError) throw new Error(actionError.message ?? 'The payment was not authorized.');
        status = paymentIntent?.status ?? status;
      }
      if (status === INTENT_STATUS.requiresPaymentMethod) throw new Error('The payment was declined. Try another payment method.');
      try {
        status = await confirmPayment(result.paymentIntentId);
      } catch {
        // Stripe accepted it; the webhook records it even if this call failed.
        setError('Your payment went through, but we could not update the balance yet. Refresh in a minute.');
        setBusy(false);
        return;
      }
      setPendingFee(null);
      onComplete(status === INTENT_STATUS.processing ? 'processing' : 'succeeded');
    } catch (err) {
      setError(errorText(err, 'The payment could not be completed.'));
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!stripe || !elements || amountCents === null) return;
    const blocked = beforePay?.() ?? null;
    if (blocked) {
      setError(blocked);
      return;
    }
    setBusy(true);
    setError(null);
    setPendingFee(null);
    // Stripe checks its own fields first; nothing is charged if they're incomplete.
    const { error: fieldError } = await elements.submit();
    if (fieldError) {
      setError(fieldError.message ?? 'Check the payment details.');
      setBusy(false);
      return;
    }
    const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({ elements });
    if (tokenError || !confirmationToken) {
      setError(tokenError?.message ?? 'Check the payment details.');
      setBusy(false);
      return;
    }
    let quoted: StripePaymentQuote;
    try {
      quoted = await quote(confirmationToken.id);
    } catch (err) {
      setError(errorText(err, 'We could not price this payment. Please try again.'));
      setBusy(false);
      return;
    }
    if (quoted.fee > 0) {
      // Shown, never assumed: the payer accepts the fee before it's charged.
      setPendingFee({ tokenId: confirmationToken.id, quote: quoted });
      setBusy(false);
      return;
    }
    await charge(confirmationToken.id, quoted);
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-4">
      <PaymentElement
        options={{
          paymentMethodOrder: PAYMENT_METHOD_TYPES,
          // Link brings its own pay-later options (Klarna) that BCE doesn't
          // take at these amounts; bank and card only.
          wallets: { link: 'never' },
        }}
      />
      {cardSurchargePercent > 0 && !pendingFee ? (
        <p className="text-portal-micro text-portal-muted">
          Credit cards add a {cardSurchargePercent}% fee, shown before you pay. Bank payments and debit cards have no fee.
        </p>
      ) : null}
      {error ? <ErrorNotice inline title="Payment not completed" message={error} /> : null}
      {pendingFee ? (
        <div className="space-y-3 rounded-portal-button border border-portal-line bg-portal-bg p-4" role="status">
          <dl className="space-y-1 text-portal-small">
            <div className="flex justify-between gap-3">
              <dt className="text-portal-muted">Payment</dt>
              <dd>{formatMoney(pendingFee.quote.amount)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-portal-muted">Credit card fee ({pendingFee.quote.feePercent}%)</dt>
              <dd>{formatMoney(pendingFee.quote.fee)}</dd>
            </div>
            <div className="flex justify-between gap-3 font-bold text-portal-ink">
              <dt>Total</dt>
              <dd>{formatMoney(pendingFee.quote.total)}</dd>
            </div>
          </dl>
          <p className="text-portal-micro text-portal-muted">Bank payments and debit cards have no fee.</p>
          <Button type="button" className="w-full justify-center" disabled={busy} onClick={() => void charge(pendingFee.tokenId, pendingFee.quote)}>
            {busy ? 'Processing…' : `Pay ${formatMoney(pendingFee.quote.total)}`}
          </Button>
          <Button type="button" variant="ghost" className="w-full justify-center" disabled={busy} onClick={() => setPendingFee(null)}>
            Use a different payment method
          </Button>
        </div>
      ) : (
        <Button type="submit" className="w-full justify-center" disabled={!stripe || busy || amountCents === null}>
          {busy ? 'Processing…' : `Pay ${amountLabel}`}
        </Button>
      )}
      <p className="flex items-center justify-center gap-1 text-portal-micro text-portal-muted">
        <LockIcon aria-hidden className="size-3" /> Payments are processed securely by Stripe
      </p>
    </form>
  );
}

/**
 * The Stripe form: bank and card fields up front, a fee shown before it's
 * charged, and charged on Pay.
 *
 * @param props - The key, what to charge, and the caller's API.
 * @returns The payment form.
 */
export function StripePayment({ publishableKey, accentColor = '#1851d1', ...form }: StripePaymentProps): React.ReactElement {
  const stripePromise = useMemo(() => stripeFor(publishableKey), [publishableKey]);
  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'payment',
        currency: 'usd',
        amount: form.amountCents ?? PLACEHOLDER_CENTS,
        paymentMethodTypes: PAYMENT_METHOD_TYPES,
        appearance: { theme: 'stripe', variables: { colorPrimary: accentColor, borderRadius: '8px' } },
      }}
    >
      <PayForm {...form} />
    </Elements>
  );
}
